// 1. Imports
import {
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Box,
    Chip,
    Tooltip,
    Collapse,
    Grid
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { When } from "react-if";
import { useState } from "react";
import NoData from "../../../no-data";
import Loading from "../../../loading";
import { MessageCenterTypes } from "../../../../types/message-center.types";
import dayjs from "dayjs";
import { GT_COLORS } from "../../../../constants/designTokens.constants";

// 2. Component Def
interface Props {
    visible: boolean;
    onClose: () => void;
    message: MessageCenterTypes.MessageBatch | null;
    records: MessageCenterTypes.MessageRecord[];
    loading?: boolean;
}

export const MessageDetailsModal: React.FC<Props> = ({ 
    visible, 
    onClose, 
    message, 
    records,
    loading = false
}) => {
    // 2.1 State and Vars
    const [expandedRows, setExpandedRows] = useState<string[]>([]);

    // Compute delivery status counts from records
    const deliveryCounts = records.reduce(
        (acc, record) => {
            const status = record.status?.toLowerCase() || '';
            if (status === 'delivered' || status === 'sent' || status === 'done') {
                acc.success++;
            } else if (status === 'failed' || status === 'error' || status === 'undelivered') {
                acc.failed++;
            } else {
                acc.other++;
            }
            return acc;
        },
        { success: 0, failed: 0, other: 0 }
    );

    // 2.2 Effects

    // 2.3 Functions
    const handleRowToggle = (recordId: string) => {
        setExpandedRows(prev =>
            prev.includes(recordId)
                ? prev.filter(id => id !== recordId)
                : [...prev, recordId]
        );
    };

    const formatPhoneNumber = (phone: string | null): string => {
        if (!phone) return 'N/A';
        // Remove any non-digit characters
        const cleaned = phone.replace(/\D/g, '');
        // Format as +1 (XXX) XXX-XXXX
        if (cleaned.length === 11 && cleaned[0] === '1') {
            return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
        } else if (cleaned.length === 10) {
            return `+1 (${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
        }
        return phone; // Return original if can't format
    };

    const getStatusTooltip = (status: string | null): string => {
        switch (status?.toLowerCase()) {
            case 'delivered': return 'Message was successfully delivered to the recipient\'s device';
            case 'sent': return 'Message was sent to the carrier but delivery confirmation has not been received yet';
            case 'done': return 'Push notification was successfully sent';
            case 'failed': return 'Message could not be sent — click this record to see error details';
            case 'undelivered': return 'Message was sent but the carrier could not deliver it — click this record to see error details';
            case 'error': return 'An error occurred while attempting to send — click this record to see error details';
            default: return 'Message is queued and waiting to be sent';
        }
    };

    const getStatusChip = (status: string | null) => {
        const statusLower = status?.toLowerCase() || '';

        let chip;
        if (statusLower === 'delivered' || statusLower === 'sent' || statusLower === 'done') {
            chip = <Chip label={status || 'Unknown'} size="small" sx={{ backgroundColor: GT_COLORS.greenLight, color: GT_COLORS.green, fontWeight: 500 }} />;
        } else if (statusLower === 'failed' || statusLower === 'error' || statusLower === 'undelivered') {
            chip = <Chip label={status || 'Unknown'} size="small" sx={{ backgroundColor: GT_COLORS.redLight, color: GT_COLORS.red, fontWeight: 500 }} />;
        } else {
            chip = <Chip label={status || 'Unknown'} size="small" sx={{ backgroundColor: GT_COLORS.badgeGrayBg, color: GT_COLORS.badgeGrayColor, fontWeight: 500 }} />;
        }

        return <Tooltip title={getStatusTooltip(status)} arrow placement="top">{chip}</Tooltip>;
    };

    const formatDateTime = (date: string | Date | null): string => {
        if (!date) return 'N/A';
        return dayjs(date).format('MM/DD/YYYY hh:mm A');
    };

    // 2.4 JSX Return
    return (
        <Dialog open={visible} onClose={onClose} maxWidth="lg" fullWidth>
            {/* Title and Close Button */}
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
                <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: GT_COLORS.textPrimary }}>
                    Message Details
                </Typography>
                <IconButton onClick={onClose} sx={{ ml: 2, color: GT_COLORS.textFaint }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            {/* Content */}
            <DialogContent>
                {/* Message Batch Info Section */}
                {message && (
                    <Box sx={{ mb: 3, p: 2, backgroundColor: GT_COLORS.pageBg, borderRadius: 2 }}>
                        <Grid container spacing={2}>
                            <Grid item sm={9}>
                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: GT_COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Title</Typography>
                                <Typography variant="body1">{message.title || 'N/A'}</Typography>
                            </Grid>
                            <Grid item sm={3} sx={{ textAlign: 'right' }}>
                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: GT_COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Delivery Date</Typography>
                                <Typography variant="body1">{formatDateTime(message.deliveryDate)}</Typography>
                            </Grid>
                            <Grid item sm={12}>
                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: GT_COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Message Body</Typography>
                                <Typography variant="body1" sx={{ wordWrap: 'break-word', overflowWrap: 'break-word', whiteSpace: 'pre-wrap' }}>
                                    {message.body || 'N/A'}
                                </Typography>
                            </Grid>
                            <Grid item sm={2}>
                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: GT_COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Recipients</Typography>
                                <Typography variant="body1">{message.recipientUserIds?.length || 0}</Typography>
                            </Grid>
                            <Grid item sm={2}>
                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: GT_COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Type</Typography>
                                <Typography variant="body1">{message.messageType || 'N/A'}</Typography>
                            </Grid>
                            <Grid item sm={2}>
                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: GT_COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Channels</Typography>
                                <Typography variant="body1">
                                    {message.channels?.map(c => c.charAt(0) + c.slice(1).toLowerCase()).join(', ') || 'N/A'}
                                </Typography>
                            </Grid>
                            <Grid item sm={6}>
                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: GT_COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>Delivery Status</Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Chip label={`${deliveryCounts.success} Successful`} size="small" variant="outlined" sx={{ borderColor: GT_COLORS.green, color: GT_COLORS.green, fontWeight: 500 }} />
                                    <Chip label={`${deliveryCounts.failed} Failed`} size="small" variant="outlined" sx={{ borderColor: GT_COLORS.red, color: GT_COLORS.red, fontWeight: 500 }} />
                                    {deliveryCounts.other > 0 && (
                                        <Chip label={`${deliveryCounts.other} Pending`} size="small" variant="outlined" sx={{ borderColor: GT_COLORS.textFaint, color: GT_COLORS.textFaint, fontWeight: 500 }} />
                                    )}
                                </Box>
                            </Grid>
                            <Grid item xs={6} sx={{ textAlign: 'right', mt: 1.5 }}>
                                <Tooltip 
                                    title={
                                        <Box>
                                            <Typography variant="caption">Batch ID: {message.id}</Typography>
                                            <br />
                                            <Typography variant="caption">Created By: {(message as any).webAppUserId || 'N/A'}</Typography>
                                        </Box>
                                    }
                                    arrow
                                    placement="right"
                                >
                                    <IconButton size="small" sx={{ ml: 1 }}>
                                        <InfoOutlinedIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Grid>
                        </Grid>
                        
                        {/* Hidden IDs in tooltip */}
                        
                    </Box>
                )}

                {/* Records Table */}
                <TableContainer sx={{ maxHeight: 400 }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: GT_COLORS.headerBg }}>
                                <TableCell width="5%"></TableCell>
                                <TableCell width="35%">
                                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: GT_COLORS.textMuted }}>
                                        Recipient Name
                                    </Typography>
                                </TableCell>
                                <TableCell width="30%">
                                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: GT_COLORS.textMuted }}>
                                        Phone Number
                                    </Typography>
                                </TableCell>
                                <TableCell width="30%">
                                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: GT_COLORS.textMuted }}>
                                        Status
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        </TableHead>

                        <When condition={!loading}>
                            <TableBody>
                                {records?.map((record) => (
                                    <>
                                        <TableRow
                                            key={record.id}
                                            sx={{
                                                ":hover": {
                                                    backgroundColor: GT_COLORS.hoverBg,
                                                    cursor: 'pointer'
                                                },
                                                '& td': { borderBottom: `1px solid ${GT_COLORS.borderLight}` },
                                            }}
                                            onClick={() => handleRowToggle(record.id ?? '')}
                                        >
                                            <TableCell>
                                                <IconButton size="small">
                                                    {expandedRows.includes(record.id ?? '') ?
                                                        <ExpandLessIcon /> : 
                                                        <ExpandMoreIcon />
                                                    }
                                                </IconButton>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {record.toUser?.firstName && record.toUser?.lastName
                                                        ? `${record.toUser.firstName} ${record.toUser.lastName}`
                                                        : 'Unknown User'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {formatPhoneNumber(record.toPhoneNumber)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                {getStatusChip(record.status)}
                                            </TableCell>
                                        </TableRow>
                                        
                                        {/* Expanded Details Row */}
                                        <TableRow>
                                            <TableCell colSpan={4} sx={{ py: 0, px: 0 }}>
                                                <Collapse in={expandedRows.includes(record.id ?? '')} timeout="auto" unmountOnExit>
                                                    <Box sx={{ p: 3, backgroundColor: GT_COLORS.headerBg }}>
                                                        <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: GT_COLORS.textPrimary, mb: 2 }}>
                                                            Message Record Details
                                                        </Typography>
                                                        <Grid container spacing={2}>
                                                            <Grid item xs={12} md={6}>
                                                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: GT_COLORS.textFaint, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                                    Record ID
                                                                </Typography>
                                                                <Typography variant="body2" sx={{ mb: 1 }}>
                                                                    {record.id || 'None'}
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs={12} md={6}>
                                                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: GT_COLORS.textFaint, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                                    User ID
                                                                </Typography>
                                                                <Typography variant="body2" sx={{ mb: 1 }}>
                                                                    {record.toUserId || 'None'}
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs={12} md={6}>
                                                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: GT_COLORS.textFaint, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                                    SMS Error Code
                                                                </Typography>
                                                                <Typography variant="body2" sx={{ mb: 1 }}>
                                                                    {record.smsErrorCode || 'None'}
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs={12} md={6}>
                                                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: GT_COLORS.textFaint, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                                    SMS Error Message
                                                                </Typography>
                                                                <Typography variant="body2" sx={{ mb: 1 }}>
                                                                    {record.smsErrorMessage || 'None'}
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs={12} md={6}>
                                                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: GT_COLORS.textFaint, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                                    Push Error Code
                                                                </Typography>
                                                                <Typography variant="body2" sx={{ mb: 1 }}>
                                                                    {record.pushErrorCode || 'None'}
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs={12} md={6}>
                                                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: GT_COLORS.textFaint, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                                    Push Error Message
                                                                </Typography>
                                                                <Typography variant="body2" sx={{ mb: 1 }}>
                                                                    {record.pushErrorMessage || 'None'}
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs={12} md={6}>
                                                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: GT_COLORS.textFaint, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                                    Created At
                                                                </Typography>
                                                                <Typography variant="body2">
                                                                    {formatDateTime(record.createdAt)}
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs={12} md={6}>
                                                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: GT_COLORS.textFaint, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                                    Updated At
                                                                </Typography>
                                                                <Typography variant="body2">
                                                                    {formatDateTime(record.updatedAt)}
                                                                </Typography>
                                                            </Grid>
                                                        </Grid>
                                                    </Box>
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    </>
                                ))}
                            </TableBody>
                        </When>
                    </Table>

                    <When condition={loading === false && (!records || records.length === 0)}>
                        <Box sx={{ p: 4 }}>
                            <NoData
                                buttonText={null}
                                onClick={() => {}}
                                title="No Message Records"
                                description="No individual message records found for this batch."
                            />
                        </Box>
                    </When>

                    <When condition={loading}>
                        <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
                            <Loading />
                        </Box>
                    </When>
                </TableContainer>
            </DialogContent>
        </Dialog>
    );
};